<template>
    <div class="sort-list">
        {{ $t('common.list.sort_by') }}

        <segment-control
            :model-value="normalizedSort"
            :options="segmentOptions"
            :variant="ComponentVariant.Sortlist"
            @update:model-value="emit('update:sort', $event)"
        />
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { SegmentControl, useI18n, ListSortOptions, ListSortOrders, ComponentVariant } from 'ihunter-pathfinder-components';

const props = defineProps({
    sortOptions: {
        type: Array,
        required: true,
    },
    sort: {
        type: String,
        required: true,
    },
});

const { t, te } = useI18n();
const emit = defineEmits(['update:sort']);

const getSortGroup = (sortKey) => {
    if (sortKey === ListSortOrders.AscName || sortKey === ListSortOrders.DescName) {
        return ListSortOptions.Name;
    }
    if (sortKey === ListSortOrders.Active || sortKey === ListSortOrders.NotActive) {
        return ListSortOptions.Active;
    }
    if (sortKey === ListSortOrders.NewestDate || sortKey === ListSortOrders.OldestDate) {
        return ListSortOptions.Date;
    }
    if (sortKey === ListSortOrders.AscType || sortKey === ListSortOrders.DescType || sortKey === ListSortOptions.Type) {
        return ListSortOptions.Type;
    }
    return sortKey;
};

const normalizedSort = computed(() => {
    return getSortGroup(props.sort);
});

const currentDirection = computed(() => {
    const currentSort = props.sort;

    if (
        currentSort === ListSortOrders.AscName ||
        currentSort === ListSortOrders.Active ||
        currentSort === ListSortOrders.NewestDate ||
        currentSort === ListSortOrders.AscType
    ) {
        return 'asc';
    }

    if (
        currentSort === ListSortOrders.DescName ||
        currentSort === ListSortOrders.NotActive ||
        currentSort === ListSortOrders.OldestDate ||
        currentSort === ListSortOrders.DescType
    ) {
        return 'desc';
    }

    // Default ASC direction for non-directional initial states ('name', 'active', 'date', 'type')
    if (
        currentSort === ListSortOptions.Name ||
        currentSort === ListSortOptions.Active ||
        currentSort === ListSortOptions.Date ||
        currentSort === ListSortOptions.Type
    ) {
        return 'asc';
    }

    return null; // For sorts that don't need arrows (like 'All')
});

const segmentOptions = computed(() => {
    return props.sortOptions.map((x) => {
        // Check if this option should display a direction arrow (Name, Active, Date)
        const showDirection = x === ListSortOptions.Name || x === ListSortOptions.Active || x === ListSortOptions.Date || x === ListSortOptions.Type;

        return {
            label: `common.list.${x}`,
            value: x,
            showArrow: showDirection,
            direction: normalizedSort.value === x ? currentDirection.value : null,
        };
    });
});
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.sort-list {
    @include Pathfinder.Eyebrow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 Pathfinder.$space-50 0 Pathfinder.$space-200;
    background-color: var(--surface);
    border: 1px solid var(--surface-glowbox);
    color: var(--on-surface-glowbox-alt);

    :deep(.segment-controls) {
        // Pull the component up and down by the border's width
        margin-bottom: -1px;
    }
}
</style>
